import { Plus } from 'lucide-react'
import type { QuizBlock } from '@/types/blocks'
import { Button, Label, Textarea } from '@teach/ui'
import { QUIZ_LIMITS } from '@/features/editor/constants/fieldLimits'
import { useContentLang } from '@/features/editor/hooks/useContentLang'
import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
import { useStableKeys } from '@/features/editor/hooks/useStableKeys'
import { charsOk } from '@/features/editor/lib/quizWordLimits'
import {
  addAnswer,
  getBody,
  removeAnswerAt,
  setAnswerText,
  setCorrectness,
  setQuestion,
} from './quizBodySync'
import { CharCount } from './shared/QuizFieldCounters'
import { QuizAnswerRow } from './QuizAnswerRow'
import { QuizFeedbackFields } from './QuizFeedbackFields'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: QuizBlock
  onChange: (next: QuizBlock) => void
}

export const ChoiceQuizEditor = ({ block, onChange }: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const lang = useContentLang()
  const answers = getBody(block, lang).answers
  const { keys, removeAt } = useStableKeys(answers.length, 'ans')
  const maxOptions = block.multiAnswer
    ? QUIZ_LIMITS.OPTIONS_MAX_MULTI
    : QUIZ_LIMITS.OPTIONS_MAX_SINGLE
  const canRemove = answers.length > QUIZ_LIMITS.OPTIONS_MIN

  const handleAddAnswer = () => {
    if (answers.length >= maxOptions) return
    onChange(addAnswer(block, lang))
  }

  const removeAnswer = (index: number) => {
    removeAt(index)
    onChange(removeAnswerAt(block, index))
  }

  const toggleCorrect = (index: number) => {
    onChange(setCorrectness(block, index, !answers[index].isCorrect, !block.multiAnswer))
  }

  const modeLabel = block.multiAnswer ? m.multiCorrect : m.singleCorrect

  return (
    <div className="space-y-4">
      <section className="space-y-1">
        <Label className="text-foreground font-medium">{m.question}</Label>
        <Textarea
          value={getBody(block, lang).question ?? ''}
          onChange={(e) => {
            if (
              charsOk(e.target.value, QUIZ_LIMITS.CHOICE_QUESTION, getBody(block, lang).question)
            ) {
              onChange(setQuestion(block, lang, e.target.value))
            }
          }}
          maxLength={QUIZ_LIMITS.CHOICE_QUESTION}
          placeholder={cm.questionPlaceholder}
          rows={3}
          className="resize-none rounded-xl"
        />
        <CharCount value={getBody(block, lang).question} max={QUIZ_LIMITS.CHOICE_QUESTION} />
      </section>

      <section className="space-y-3">
        <Label className="text-foreground font-medium">
          {m.answerOptions}{' '}
          <span className="font-normal text-muted-foreground/70">· {modeLabel}</span>
        </Label>
        <div className="flex flex-col gap-2">
          {answers.map((a, i) => (
            <QuizAnswerRow
              key={keys[i]}
              index={i}
              answer={a}
              charMax={QUIZ_LIMITS.OPTION_CHARS}
              canRemove={canRemove}
              onChangeText={(idx, text) => onChange(setAnswerText(block, lang, idx, text))}
              onToggleCorrect={toggleCorrect}
              onRemove={removeAnswer}
            />
          ))}
          {answers.length < maxOptions ? (
            <Button variant="secondary" size="sm" onClick={handleAddAnswer}>
              <Plus className="mr-1.5 h-4 w-4" />
              {m.addAnswer}
            </Button>
          ) : null}
        </div>
      </section>

      <QuizFeedbackFields block={block} onChange={onChange} />
    </div>
  )
}
